import { normalizeHost, stripTLD, stripWww } from "@/utils/url-utils";

/**
 * Market configuration mapping domains/subdomains to market IDs
 */
/** To Do 
* Add dev and qa domains for other sites when available 
* This would include PR environments that have their own domains
* For now all localhost, PR, web, and qa domains will map to market ID "1"
* since we are a couple months away from replatfroming partner sites
* Dan London - 12/11/2025
* */
const MARKET_CONFIG = {
  "gocollette": "1",
  "mvcowner.collette": "8",
  "memberchoicevacations": "10",
  "interval.collette": "19",
  "hvcplatinum.collette": "20",
  "hvcportfolio.collette": "21"
} as const;

const DEFAULT_MARKET_ID = "1";

/**
 * Parse hostname to derive market key according to business rules:
 * - For *.collette.com: key = `${subdomain}.collette` (e.g., interval.collette)
 * - For root domains: key = domain name (e.g., gocollette, memberchoicevacations)
 */
export const parseMarketKeyFromHostname = (rawHost: string): string | null => {
  const host = normalizeHost(rawHost);
  if (!host) return null;

  const noWww = stripWww(host);      // Remove www prefix
  const base = stripTLD(noWww);      // Remove .com suffix

  // Handle *.collette.com subdomains
  if (noWww.endsWith(".collette.com")) {
    const subdomain = noWww.split(".")[0];
    if (!subdomain || subdomain === "collette") return null;
    return `${subdomain}.collette`;
  }

  // Handle root domains (gocollette.com, memberchoicevacations.com, etc.)
  return base;
};

/**
 * Get market ID from a market key
 */
export const getMarketIdByKey = (marketKey: string): string => MARKET_CONFIG[marketKey as keyof typeof MARKET_CONFIG] || DEFAULT_MARKET_ID;

/**
 * Resolve market ID from URL string, hostname, or current browser URL
 * @param urlOrHost - Full URL, hostname, or undefined to use current location
 * @returns Market ID string (defaults to "1" if no match found)
 */
export const resolveMarketId = (urlOrHost?: string): string => {
  let hostname: string | undefined;

  if (urlOrHost) {
    try {
      // Try to parse as full URL first
      const url = new URL(urlOrHost.includes("://") ? urlOrHost : `https://${urlOrHost}`);
      hostname = url.hostname;
    } catch {
      // Not a valid URL, treat as hostname
      hostname = urlOrHost;
    }
  } else if (typeof window !== "undefined") {
    // Use current browser location
    hostname = window.location.hostname;
  }

  if (!hostname) return DEFAULT_MARKET_ID;

  const marketKey = parseMarketKeyFromHostname(hostname);
  return marketKey ? getMarketIdByKey(marketKey) : DEFAULT_MARKET_ID;
};
